import React from "react";
import RNRestart from 'react-native-restart';
import { Alert, Platform } from 'react-native';
import { sub as subDate, addDays as _addDays, format, formatDistance } from "date-fns";
import { STATE, APIData } from "../constants";
import _ from "underscore";

import { useEffect } from "react";
import { NavigationProp, useFocusEffect } from "@react-navigation/native";


export { differenceInMonths, differenceInDays, parseISO } from 'date-fns';

type CustomHook = [APIData | any, (data: any, isCore?: boolean) => void];

export const reloadApp = () => Platform.OS === "web" ? window.location.reload() : RNRestart.restart();

export const onNavigation = (
  navigation: NavigationProp<any>,
  hookList: Array<CustomHook>
) => {
  useFocusEffect(
    React.useCallback(() => {
      // Clear and re-initiate the state when the screen gains focus
      hookList.forEach(([res = {}, setRes]) =>
        setRes({ ...{ state: STATE.READY }, ...res })
      );
      // Clean up the listener when the component is unmounted
      return () => {
        hookList.forEach(([res = {}, setRes]) =>
          setRes({ ...{ state: STATE.CANCEL }, ...res })
        );
      };
    }, [])
  );
};

export const onStateChange = (res: APIData, state: STATE, callback?: (data?: any) => void): void => {
  useEffect(() => {
    if (res?.state === state) {
      callback && callback(res?.data);
    }
  }, [res]);
};

export const printDecimalOf = (amount: any, digits: number = 2) => {
  return Number.parseFloat(amount || 0).toFixed(digits);
};

export const isWeb = () => Platform.OS === "web";

export const isAlpha = (char: string) => {
  return /^[a-zA-Z]+$/.test(char);
};

// Function to change the key name for all objects in the array
export const changeKeyName = (
  objectsArray: Array<any>,
  oldKeyName: string,
  newKeyName: string
) => {
  return _.map(objectsArray, (obj) => {
    if (_.has(obj, oldKeyName)) {
      obj[newKeyName] = obj[oldKeyName];
      delete obj[oldKeyName];
    }
    return obj;
  });
};

export const deepEqual = (object1: any = null, object2: any = null) => {
  if (object1 === null && object2 === null) return true;
  if (object1 === null || object2 === null) return false;

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }
  return true;
};

export const updateData = (
  update: any,
  initial: APIData,
  setHook: Function
) => {
  const updatedData = { ...initial, data: [{ ...initial.data[0], ...update }] };
  setHook && setHook(updatedData);
};

export const isObject = (object: Object) => {
  return object != null && typeof object === "object";
};

export const uppercase = (word: string) => {
  const lower = word.toLowerCase();
  return word.charAt(0).toUpperCase() + lower.slice(1);
};

// every character are represented in their lower case
export const lowercase = (word: string) => {
  return word.toLocaleLowerCase();;
};

export const capitalize = (word: string) => {
  let caps = "";
  for (let i = 0, len = word.length; i < len; i++) {
    caps += word.charAt(i).toUpperCase();
  }
  return caps;
};

export const sentenceCase = (sentences: string) => {
  sentences = lowercase(sentences)
    // Convertion into an array
    .split(".")
    // Remove extra space
    .map(sentence => sentence.trim())
    // Upper the case of the first character for each sentence
    .map(sentence => sentence.charAt(0).toUpperCase() + sentence.slice(1))
    // Return upper case sentence
    .join(". ");

  return sentences;
};

export const truncate = (str: string, charCount = 35) => {
  if (str.length > charCount) {
    return str.slice(0, charCount) + ` ...`;
  } else {
    return str;
  }
};

export const getDomainFromUrl = (url: string) => {
  let domain = new URL(url).hostname;
  // Remove subdomain if present
  domain = domain.split(".").slice(-2).join(".");
  return domain;
}

export const formatUrl = (baseUrl: string, url: string) => {
  // Check if the URL is absolute
  const isAbsolute = /^https?:\/\//i.test(url);

  // If the URL is absolute, return it as is
  if (isAbsolute) {
    return url;
  }

  // If the URL is relative, prepend the domain / baseUrl <baseUrl>
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
}

export const increaseBrightness = (hex: string, percent: number) => {
  // strip the leading # if it's there
  hex = hex.replace(/^\s*#|\s*$/g, "");

  // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, "$1$1");
  }

  const r = parseInt(hex.substr(0, 2), 16),
    g = parseInt(hex.substr(2, 2), 16),
    b = parseInt(hex.substr(4, 2), 16);

  return (
    "#" +
    (0 | ((1 << 8) + r + ((256 - r) * percent) / 100)).toString(16).substr(1) +
    (0 | ((1 << 8) + g + ((256 - g) * percent) / 100)).toString(16).substr(1) +
    (0 | ((1 << 8) + b + ((256 - b) * percent) / 100)).toString(16).substr(1)
  );
};

export const dateDistance = (date1 = new Date(), date2 = new Date()) => {
  return formatDistance(date1, date2, { addSuffix: true });
};

export const formatDate = (
  date: string | number | Date | null,
  formatting = "MMMM dd, yyyy"
): string => {
  return date ? format(dateToUTC(date) as Date, formatting) : "";
};

export const addDays = (
  date: string | number | Date | null,
  amount: number = 0,
  formatting: string | undefined | null = "MMMM dd, yyyy"
) => {
  if (!date) return "";
  const newDate = _addDays(dateToUTC(date) as Date, amount);
  return formatting ? formatDate(newDate, formatting) : newDate;
};

export const dateToUTC = (date: string | number | Date | null): Date | null => {
  if (date === null) return null;
  const d = new Date(date);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds()
  );
};

export const sameDay = (
  cp1: string | number | Date | null,
  cp2: string | number | Date | null
) => {
  if (cp1 === null && cp2 === null) return true;
  if (cp1 === null || cp2 === null) return false;

  const d1 = new Date(cp1);
  const d2 = new Date(cp2);

  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
};

export const hoursToStandard = (hours: number) => {
  const suffix = hours >= 12 ? "PM" : "AM";
  return ((hours + 11) % 12) + 1 + suffix;
};

export const lastDates = (size = 10) => {
  const days: Date[] = [];
  let current = new Date();

  while (days.length < size) {
    days.push(current);
    current = new Date(subDate(current, { days: 1 }));
  }
  return days;
};

export const lastMonths = (size = 10) => { };

export const getMonthToDate = (date: Date) => {
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  return [firstDay, date];
};

export const zeroPad = (num: number | string, places: number) => {
  return String(num).padStart(places, "0");
};

export const firstDateOfMonth = (date: string | number | Date) => {
  const d = new Date(date);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0);
};

export const firstDateOfPrevMonth = (date: string | number | Date) => {
  const d = new Date(date);
  return new Date(d.getUTCFullYear(), d.getUTCMonth() - 1, 1, 0, 0, 0);
};
